/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import { gql } from 'graphql-request';

export const listOrdersGql = gql`
  query listOrders(
    $limit: Int = 25
    $offset: Int = 0
    $filter: OrdersFilter
    $sortBy: OrdersVerificationSortBy = { sortKey: shipByDate, sortOrder: DESC }
  ) {
    listOrders(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      orders: orders {
        orderId
        orderStatus
        requestNeedsByDate
        lastUpdated
        createdAt
        waitingOnFill
        totalInOrder
        fulfillingPharmacyId
        patient {
          mpi: patientId
          patientDemographics {
            firstName
            lastName
          }
        }
        partner {
          partnerName
          partnerUuid
        }
        lineItems {
          itemName
          needsByDate
          shipByDate
          requestedDate
          itemType
          lineItemId
          quantity
          rxNumber
          lineItemStatus
          form
          strength
          fillType
          shippingOption
          inBatchShipmentQueue
          isBusinessAddress
          isOtc
        }
        partnerAccountId
        fillType
        state
        source
        isReshipment
      }
    }
  }
`;

export const fulfillProductByRxNumberGql = gql`
  mutation fulfillProductByRxNumber($rxNumber: String!, $drugDispensed: [DrugDispensedInput!]!) {
    fulfillProductByRxNumber(rxNumber: $rxNumber, drugDispensed: $drugDispensed) {
      orderId
      status: orderStatus
    }
  }
`;

// The scanned vial's NDC replaces the one assigned to the Rx rather than matching
// it — used when the pharmacist is substituting a same-product, different-NDC
// vial (see ScanSessionContext's vialNdcDiffersFromRx). Same shape as
// fulfillProductByRxNumber; the backend gates the substitution on the product id
// still matching.
export const overrideFulfillmentNdcGql = gql`
  mutation overrideFulfillmentNdc($rxNumber: String!, $drugDispensed: [DrugDispensedInput!]!) {
    overrideFulfillmentNdc(rxNumber: $rxNumber, drugDispensed: $drugDispensed) {
      orderId
      status: orderStatus
    }
  }
`;

export const manageProductFulfillmentGql = gql`
  mutation manageProductFulfillmentStatus(
    $mpi: String!
    $orderId: String!
    $lineItemId: String!
    $state: ProductFulfillmentStatus!
    $failureReason: ProductFulfillmentFailureReason
    $lotAllocations: [LotAllocationInput!]
    $unassignedFill: Boolean
    $markScanned: Boolean
  ) {
    manageProductFulfillmentStatus(
      lineItemId: $lineItemId
      mpi: $mpi
      orderId: $orderId
      state: $state
      failureReason: $failureReason
      lotAllocations: $lotAllocations
      unassignedFill: $unassignedFill
      markScanned: $markScanned
    ) {
      orderId
      status: orderStatus
      orderCost
      requestId
      requestNeedsByDate
      orderType
      isProcessedExternal
      fulfillingPharmacyId
      fillType
      shippingAddress {
        nameOnAddress
        line1
        line2
        city
        state
        zip
      }
      partner {
        partnerName
      }
      lineItems {
        itemName
        trackingNumber
        needsByDate
        shipByDate
        requestedDate
        itemType
        lineItemId
        hasPinnedNote
        rxNumber
        oldRxNumber
        showOldRxNumber
        strength
        form
        quantity
        lineItemStatus
        lastUpdated
        cost
        prescriptionId
        taggedForReview
        isPrinted
        documentId
        processingState
        inBatchShipmentQueue
        isBusinessAddress
        shippingOption
        addressDirectoryId
        prescribedQuantity
        reshipmentReason
        isLabelCreated
      }
      partnerAccountId
      source
    }
  }
`;

export const createOrderShipmentGql = gql`
  mutation createOrderShipment(
    $mpi: String
    $shipmentType: String
    $lineItemIds: [String!]!
    $shipmentMode: ShipmentMode!
    $carrierCode: String
    $trackingNumber: String
    $externalShipmentId: String
    $shipDate: String
    $shipTime: String
    $isShipmentFromPrx: Boolean
    $addressDirectoryId: String
    $nameOnAddress: String
    $isBatchShipment: Boolean
  ) {
    createOrderShipment(
      mpi: $mpi
      shipmentType: $shipmentType
      lineItemIds: $lineItemIds
      shipmentMode: $shipmentMode
      carrierCode: $carrierCode
      trackingNumber: $trackingNumber
      externalShipmentId: $externalShipmentId
      shipDate: $shipDate
      shipTime: $shipTime
      isShipmentFromPrx: $isShipmentFromPrx
      addressDirectoryId: $addressDirectoryId
      nameOnAddress: $nameOnAddress
      isBatchShipment: $isBatchShipment
    ) {
      orderShipmentId
      shipmentData
      shipmentSpType
      shipmentStatus
      spOrderId
      orderIds
    }
  }
`;

export const verifyOrderGql = gql`
  mutation verifyOrder(
    $mpi: UUID!
    $orderId: String!
    $shipmentType: String
    $lineItemIds: [String!]
    $isAutoShipEnabled: Boolean
    $skipAddressValidation: Boolean
    $carrierCode: String
    $trackingNumber: String
    $externalOrderId: String
    $shipmentMode: ShipmentMode
    $shipDate: String
    $shipTime: String
  ) {
    verifyOrder(
      mpi: $mpi
      orderId: $orderId
      lineItemIds: $lineItemIds
      shipmentType: $shipmentType
      isAutoShipEnabled: $isAutoShipEnabled
      skipAddressValidation: $skipAddressValidation
      carrierCode: $carrierCode
      trackingNumber: $trackingNumber
      externalOrderId: $externalOrderId
      shipmentMode: $shipmentMode
      shipDate: $shipDate
      shipTime: $shipTime
    ) {
      __typename
      ... on PatientOrder {
        orderId
        status: orderStatus
        orderCost
        shippingAddress {
          line1
          line2
          city
          state
          zip
        }
        partner {
          partnerName
        }
        lineItems {
          lineItemId
          lineItemStatus
          cost
        }
        partnerAccountId
      }
      ... on Addresses {
        addresses {
          line1
          line2
          city
          state
          zip
        }
      }
    }
  }
`;

export const listOrdersForShipmentGql = gql`
  query listOrdersForShipment(
    $limit: Int = 25
    $offset: Int = 0
    $filter: OrdersFilter
    $sortBy: OrdersSortBy = { sortKey: shipByDate, sortOrder: DESC }
  ) {
    listOrdersForShipment(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      orders: orders {
        orderId
        orderStatus
        requestNeedsByDate
        lastUpdated
        patient {
          mpi: patientId
          patientDemographics {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const cancelOrderGql = gql`
  mutation cancelOrder(
    $orderId: String!
    $cancelReason: CancelReason!
    $cancelNote: CreateNoteInput!
  ) {
    cancelOrder(orderId: $orderId, cancelReason: $cancelReason, cancelNote: $cancelNote) {
      orderId
    }
  }
`;
export const updateOrderAssociationGql = gql`
  mutation updateOrderAssociation(
    $patientId: String!
    $partnerId: String!
    $practiceId: String!
    $orderId: String!
    $partnerAccountId: String
    $includeAllFacilities: Boolean
  ) {
    updateOrderAssociation(
      input: {
        patientId: $patientId
        partnerId: $partnerId
        practiceId: $practiceId
        orderId: $orderId
        partnerAccountId: $partnerAccountId
        includeAllFacilities: $includeAllFacilities
      }
    ) {
      count
      results: result {
        orderId
        requestId
        fulfillingPharmacyId
        status: orderStatus
        requestNeedsByDate
        orderCost
        orderType
        isProcessedExternal
        fillType
        reshipmentOf
        source
        partner {
          partnerName
        }
        partnerAccountId
        shippingAddress {
          line1
          line2
          city
          state
          zip
        }
        lineItems {
          itemName
          needsByDate
          shipByDate
          requestedDate
          itemType
          lineItemId
          hasPinnedNote
          rxNumber
          oldRxNumber
          showOldRxNumber
          strength
          form
          quantity
          lineItemStatus
          lastUpdated
          cost
          prescriptionId
          taggedForReview
          isPrinted
          documentId
          processingState
          inBatchShipmentQueue
          isBusinessAddress
          shippingOption
          addressDirectoryId
          prescribedQuantity
        }
      }
    }
  }
`;

// POM-6148: reduces the line item to the retained quantity, marks it VOID and records the
// remainder as fill_later_item rows at status SCHEDULED. No order is created here — POM-6150
// creates it on each fill date.
//
// Returns the rows exactly as stored, ordered by scheduledFor. The input takes an array of
// line items so the same mutation serves a bulk screen; the dialog sends one.
export const splitLineItemQuantityGql = gql`
  mutation splitLineItemQuantity($input: SplitLineItemQuantityInput!) {
    splitLineItemQuantity(input: $input) {
      fillLaterItemId
      referenceOrderId
      referenceItemId
      referenceRxNumber
      splitQuantity
      scheduledFor
      status
    }
  }
`;

// POM-6180: moves EVERY line item on the order to Fill Later at its full quantity on one date
// and parks the order (status VOID) until then. Nothing is created; the fill date releases the
// order. Returns the fill_later_item rows written, one per line item.
export const moveOrderToFillLaterGql = gql`
  mutation moveOrderToFillLater($input: MoveOrderToFillLaterInput!) {
    moveOrderToFillLater(input: $input) {
      orderId
      scheduledFor
      items {
        fillLaterItemId
        referenceOrderId
        referenceItemId
        referenceRxNumber
        splitQuantity
        scheduledFor
        status
      }
    }
  }
`;

export const cancelLineItemGql = gql`
  mutation cancelLineItem(
    $orderId: String!
    $lineItemId: String!
    $cancelReason: CancelReason!
    $cancelNote: CreateNoteInput!
  ) {
    cancelLineItem(
      orderId: $orderId
      lineItemId: $lineItemId
      cancelReason: $cancelReason
      cancelNote: $cancelNote
    ) {
      orderId
    }
  }
`;

export const cancelBulkLineItemsGql = gql`
  mutation cancelBulkLineItems(
    $lineItemIds: [String!]!
    $cancelReason: CancelReason!
    $cancelNote: BulkCancelNoteInput!
  ) {
    cancelBulkLineItems(
      lineItemIds: $lineItemIds
      cancelReason: $cancelReason
      cancelNote: $cancelNote
    )
  }
`;

export const orderNeedsReviewGql = gql`
  mutation markOrderForExternalProcessing($orderId: String!, $mpi: UUID!) {
    markOrderForExternalProcessing(orderId: $orderId, mpi: $mpi)
  }
`;

export const updateOrderAddressGql = gql`
  mutation updateOrderAddress(
    $orderId: String!
    $addressInput: AddPatientAddressInput!
    $isAutoShipNeeded: Boolean
  ) {
    updateOrderAddress(
      orderId: $orderId
      addressInput: $addressInput
      isAutoShipNeeded: $isAutoShipNeeded
    ) {
      __typename
      ... on PatientAddress {
        address {
          addressLine1: line1
          addressLine2: line2
          city
          state
          zip
        }
        nameOnAddress
        tags
        patientAddressId
        deliveryInstructions
      }
      ... on Addresses {
        addresses {
          line1
          line2
          city
          state
          zip
        }
      }
    }
  }
`;

export const prescriptionNeedsReviewGql = gql`
  mutation prescriptionNeedsReview(
    $prescriptionUUIDs: [String]!
    $patientId: UUID!
    $reviewTarget: PrescriptionReviewStatus
  ) {
    prescriptionNeedsReview(
      prescriptionUUIDs: $prescriptionUUIDs
      patientId: $patientId
      reviewTarget: $reviewTarget
    )
  }
`;

export const changePrescriptionReviewReasonGql = gql`
  mutation changePrescriptionReviewReason(
    $prescriptionUUID: String!
    $patientId: UUID!
    $lineItemId: String!
    $reviewTarget: PrescriptionReviewStatus
  ) {
    changePrescriptionReviewReason(
      prescriptionUUID: $prescriptionUUID
      patientId: $patientId
      lineItemId: $lineItemId
      reviewTarget: $reviewTarget
    )
  }
`;

export const getOrdersGql = gql`
  query listPomOrders(
    $limit: Int = 25
    $offset: Int = 0
    $filter: PomOrdersFilter
    $sortBy: PomOrdersSortBy = { sortKey: createdAt, sortOrder: DESC }
  ) {
    listPomOrders(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result {
        orderId
        referenceId
        status
        lineItemId
        rxNumber
        patient {
          mpi
          firstName
          lastName
        }
        createdAt
        updatedAt
        partnerName
        partnerAccountId
        batchShipmentId
        inBatchShipping
        isBusinessAddress
        shippingOption
        processingState
        isOtc
        drugName
        strength
        lineItemStatus
        ndc
        quantity
        isScanned
        fulfillingPharmacyId
        productId
      }
    }
  }
`;

export const reverifyOrderGql = gql`
  mutation reverifyOrder($orderId: String!, $mpi: UUID!) {
    reverifyOrder(orderId: $orderId, mpi: $mpi) {
      orderId
      status: orderStatus
    }
  }
`;

export const listProcessedExternalOrdersGql = gql`
  query listProcessedExternalOrders(
    $limit: Int
    $offset: Int
    $filter: ExternalSystemOrderFilter
    $sortBy: ExternalSystemOrderSortBy
  ) {
    listProcessedExternalOrders(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      orders {
        orderId
        status
        createdAt
        partnerName
        partnerAccount
        patientFirstName
        patientLastName
        patientId
      }
    }
  }
`;

export const listBatchShipmentsGql = gql`
  query listBatchShipments(
    $limit: Int
    $offset: Int
    $sortOrder: SortOrder
    $filter: BatchShipmentsFilter
  ) {
    listBatchShipments(limit: $limit, offset: $offset, sortOrder: $sortOrder, filter: $filter) {
      count
      result {
        addressDirectoryId
        totalItems
        address {
          addressLine1
          addressLine2
          addressZip
          addressState
          addressCity
        }
        partnerName
        practiceName
        partnerId
        practiceId
        isBusinessAddress
      }
    }
  }
`;

export const listShipmentLineItemsGql = gql`
  query listShipmentLineItems(
    $addressDirectoryId: String!
    $limit: Int
    $offset: Int
    $partnerId: String!
    $practiceId: String!
  ) {
    listShipmentLineItems(
      addressDirectoryId: $addressDirectoryId
      limit: $limit
      offset: $offset
      partnerId: $partnerId
      practiceId: $practiceId
    ) {
      count
      result {
        drugName
        rxNumber
        orderId
        lineItemId
        needsByDate
        shipByDate
        lineItemStatus
        shippingOption
        isOtc
        patient {
          mpi
          firstName
          lastName
          middleName
          email
          mobilePhone
          gender
          dob
        }
        facilityName
        isTaggedForReview
        rxPrefix
        fulfillingPharmacyState
        fulfillingPharmacyId
        labelRegenerationStatus
      }
    }
  }
`;

export const getRecipientAddressesGql = gql`
  query getRecipientAddresses($orderIds: [String!]!) {
    getRecipientAddresses(orderIds: $orderIds) {
      mpi
      patientName
      addresses {
        patientAddressId
        nameOnAddress
        tags
        isVerified
        isBusinessAddress
        deliveryInstructions
        addressDirectoryId
        lastUsedTimestamp
        address {
          line1
          line2
          city
          state
          zip
        }
      }
    }
  }
`;

export const updateAddressOrderLineItemStatusGql = gql`
  mutation updateAddressOrderLineItemStatus(
    $lineItemIds: [UUID!]!
    $addressDirectoryId: UUID!
    $status: AddressOrderLineItemsStatus
  ) {
    updateAddressOrderLineItemStatus(
      lineItemIds: $lineItemIds
      addressDirectoryId: $addressDirectoryId
      status: $status
    )
  }
`;

export const createShipmentForLineItemsGql = gql`
  mutation createShipmentForLineItems(
    $lineItemIds: [String!]!
    $addressDirectoryId: String!
    $nameOnAddress: String!
    $shippingOption: String!
    $carrierCode: String
    $trackingNumber: String
    $shipmentMode: ShipmentMode!
    $externalShipmentId: String
    $shipDate: String
    $shipTime: String
    $insuranceAmountInUSD: Float
  ) {
    createShipmentForLineItems(
      lineItemIds: $lineItemIds
      addressDirectoryId: $addressDirectoryId
      nameOnAddress: $nameOnAddress
      shippingOption: $shippingOption
      carrierCode: $carrierCode
      trackingNumber: $trackingNumber
      shipmentMode: $shipmentMode
      externalShipmentId: $externalShipmentId
      shipDate: $shipDate
      shipTime: $shipTime
      insuranceAmountInUSD: $insuranceAmountInUSD
    ) {
      orderIds
    }
  }
`;

export const listShippingOptionsGql = gql`
  query listShippingOptions($practiceId: String, $partnerUUID: String) {
    listShippingOptions(practiceId: $practiceId, partnerUUID: $partnerUUID) {
      optionCode
      shippingOption
      daysToAdd
      carrierCode
      carrierShipmentCode
      isDefault
    }
  }
`;

export const getBatchShipmentsGql = gql`
  query getBatchShipments($limit: Int, $offset: Int, $batchShipmentId: String!) {
    getBatchShipments(limit: $limit, offset: $offset, batchShipmentId: $batchShipmentId) {
      count
      response {
        lineItemId
        rxNumber
        patientId
        patientFirstName
        patientLastName
        drugName
        orderId
        trackingNumber
        createdAt
        fillType
      }
    }
  }
`;

export const listUnReconcileOrdersGql = gql`
  query listUnReconcileOrders($limit: Int, $offset: Int, $filter: UnReconcileOrdersFilter) {
    listUnReconcileOrders(limit: $limit, offset: $offset, filter: $filter) {
      count
      result {
        orderNumber
        spOrderId
        shipmentReconcileId
        trackingNumber
        shipmentSpType
        patientName
        isReconciled
        shipmentData
        createdAt
        modifiedAt
      }
    }
  }
`;

export const createReshipmentGql = gql`
  mutation createReshipment(
    $input: [CreateReshipmentLineItemInput!]!
    $patientAddressId: UUID
    $addressDirectoryId: String!
    $isManuallyVerified: Boolean
    $isValidationRequired: Boolean
    $deliveryInstructions: String
  ) {
    createReshipment(
      input: $input
      patientAddressId: $patientAddressId
      addressDirectoryId: $addressDirectoryId
      isManuallyVerified: $isManuallyVerified
      isValidationRequired: $isValidationRequired
      deliveryInstructions: $deliveryInstructions
    )
  }
`;

export const getRxProofUploadUrlsGql = gql`
  mutation getRxProofUploadUrls($input: RxProofUploadInput!) {
    getRxProofUploadUrls(input: $input) {
      filename
      uploadUrl
      removeUrl
      key
    }
  }
`;

export const createPomOrderGql = gql`
  mutation createPomOrder($input: CreatePomOrderInput!) {
    createPomOrder(input: $input) {
      responseType
      reasons
      # POM-6279: how ROUTING_RULES routed the order. Both null on REJECTED.
      routingOutcome
      lineRouting {
        lineIndex
        outcome
      }
    }
  }
`;

export const createPOMOTCOrderGql = gql`
  mutation createPOMOTCOrder($input: POMOTCOrderInput!) {
    createPOMOTCOrder(input: $input)
  }
`;

export const deleteIrreconciledOrderGql = gql`
  mutation deleteUnReconcileOrders($spOrderIds: [String!]!) {
    deleteUnReconcileOrders(spOrderIds: $spOrderIds) {
      shipmentReconcileId
      spOrderId
      isReconciled
      isDeleted
      shipmentData
      shipmentSpType
      createdAt
      modifiedAt
    }
  }
`;

export const getOrderSuggestionsForShipstationOrdersGql = gql`
  query GetOrderSuggestions(
    $limit: Int
    $offset: Int
    $input: GetOrderSuggestionsInput
    $filter: OrderSuggestionFilter
  ) {
    getOrderSuggestions(limit: $limit, offset: $offset, input: $input, filter: $filter) {
      count
      result {
        orderId
        orderShipmentId
        patient {
          mpi
          firstName
          lastName
          middleName
          email
          mobilePhone
          gender
          dob
        }
        partner {
          partnerName
          partnerUuid
        }
        lineItems {
          itemName
          needsByDate
          shipByDate
          requestedDate
          itemType
          lineItemId
          lineItemStatus
          rxNumber
          strength
          form
          quantity
          lastUpdated
          hasPinnedNote
          autoFill
          cost
          prescriptionId
          taggedForReview
          isPrinted
          documentId
          fillType
          directions
          rxNotes
          expirationDate
          refillsRemaining
          oldRxNumber
          showOldRxNumber
          processingState
          addressDirectoryId
          inBatchShipmentQueue
          isBusinessAddress
          shippingOption
          fulfillingPharmacyId
          prescribedQuantity
          reshipmentReason
          reviewReason
        }
      }
    }
  }
`;

export const associateOrderShipmentGql = gql`
  mutation associateOrderShipment($input: AssociateOrderShipmentInput!) {
    associateOrderShipment(input: $input) {
      orderShipmentId
      spOrderId
    }
  }
`;

export const voidShipmentGql = gql`
  mutation voidShipment(
    $mpi: String!
    $lineItemIds: [String!]!
    $orderId: String!
    $orderStatus: String!
  ) {
    voidShipment(mpi: $mpi, lineItemIds: $lineItemIds, orderId: $orderId, orderStatus: $orderStatus)
  }
`;

export const getOrderShipmentsGql = gql`
  query getOrderShipments(
    $limit: Int
    $offset: Int
    $sortBy: ShipmentOrderSortBy
    $filter: ShipmentOrderFilter
  ) {
    getOrderShipments(limit: $limit, offset: $offset, sortBy: $sortBy, filter: $filter) {
      count
      result {
        shipmentId
        spOrderId
        recipientName
        partnerName
        practiceName
        fulfillingPharmacyId
        serviceCode
        carrierCode
        packageCode
        trackingNumber
        shipmentCreatedAt
        shipmentShippedAt
        voidedTimestamp
        isBatchShipment
        shipmentStatus
        itemCount
        shipmentPhoneNumber
        cost
        insuranceAmountInUSD
        shipDate
        enableSaturdayDelivery
        dimensions {
          length
          width
          height
          units
        }
        shipmentWeight {
          units
          value
        }
        shipmentAddress {
          line1
          line2
          city
          state
          zip
          isVerified
          addressValidity
          addressDeficiencies
          addressDirectoryId
        }
        originalShipmentAddress {
          line1
          line2
          city
          state
          zip
        }
        originalServiceCode
        validatedShipmentAddress {
          line1
          line2
          city
          state
          zip
          addressValidity
          addressDeficiencies
          addressDirectoryId
        }
        shipmentItems {
          orderId
          lineItemId
          weight {
            units
            value
          }
          sku
          quantity
          name
        }
      }
    }
  }
`;

export const transferOrderDetailsGql = gql`
  query transferOrderDetails($orderId: String!) {
    transferOrderDetails(orderId: $orderId) {
      orderId
      patientName
      fulfillingPharmacyId
      status
      patientGender
      patientDateOfBirth
      lineItems {
        lineItemId
        itemName
        overriddenItemName
        itemType
        lineItemStatus
        processingState
        rxNumber
        prescriptionId
        quantity
        directions
        refillsRemaining
        writtenDate
        prescriberName
        needsByDate
        shipByDate
        requestedDate
        strength
        form
        lastUpdated
        taggedForReview
        inBatchShipmentQueue
        fulfillingPharmacyId
        isOtc
      }
    }
  }
`;

export const generateLabelForShipmentGql = gql`
  mutation generateLabelForShipment($shipmentId: String!) {
    generateLabelForShipment(shipmentId: $shipmentId) {
      isValidShipment
      shipment {
        shipmentId
        carrierServiceCode
        carrierPackageCode
        status
        shippedTimestamp
        labelUrl
        cost
        clientMetadata
        trackingNumber
        orderWeight {
          units
          value
        }
        packageDimensions {
          length
          width
          height
          units
        }
      }
    }
  }
`;

export const getShipmentLabelGql = gql`
  query getShipmentLabel($shipmentId: String!) {
    getShipmentLabel(shipmentId: $shipmentId)
  }
`;

export const updateShipmentGql = gql`
  mutation updateShipment(
    $shipmentId: String!
    $referenceId: String!
    $input: UpdateShipmentInput!
  ) {
    updateShipment(shipmentId: $shipmentId, referenceId: $referenceId, input: $input) {
      shipmentId
      referenceId
      cost
      carrier
      carrierServiceCode
      carrierPackageCode
      clientMetadata
      addressValidity
      addressDeficiencies
      orderWeight {
        units
        value
      }
      packageDimensions {
        length
        width
        height
        units
      }
      shippingAddress {
        nameOnAddress
        phoneNumber
        addressDirectoryId
        address {
          line1
          line2
          city
          state
          zip
        }
      }
      originalShippingAddress {
        addressDirectoryId
        address {
          line1
          line2
          city
          state
          zip
        }
      }
      originalCarrierServiceCode
      deliveryInstructions
      saturdayShipping
      signatureRequired
      shipDate
      validatedShippingAddress {
        nameOnAddress
        phoneNumber
        addressDirectoryId
        address {
          line1
          line2
          city
          state
          zip
        }
      }
      tags {
        ruleId
        label
        color
        icon
        tooltip
      }
    }
  }
`;

export const cancelPrxShipmentGql = gql`
  mutation cancelShipment($trackingNumber: String!) {
    cancelShipment(trackingNumber: $trackingNumber) {
      shipmentId
      referenceId
      status
      trackingNumber
    }
  }
`;

export const deletePrxShipmentGql = gql`
  mutation deleteShipmentRequest($shipmentId: String!, $reason: String, $cancelType: String) {
    deleteShipmentRequest(shipmentRequestId: $shipmentId, reason: $reason, cancelType: $cancelType)
  }
`;

export const cancelOrderShipmentGql = gql`
  mutation cancelOrderShipment($shipmentId: String!) {
    cancelOrderShipment(shipmentId: $shipmentId)
  }
`;

export const validateShipmentAddressGql = gql`
  query validateAddress($input: ShipmentAddressInput!) {
    validateAddress(input: $input) {
      validity
      resolvedAddress {
        line1
        line2
        city
        state
        zip
      }
      originalAddress {
        line1
        line2
        city
        state
        zip
      }
      deficiencies
      addressDirectoryId
    }
  }
`;

export const getShipmentCountsGql = gql`
  query CountShipments($filter: ShipmentFilter, $groupBy: [String!]) {
    countShipments(filter: $filter, groupBy: $groupBy) {
      buckets {
        fulfillingPharmacyId
        status
        count
      }
      totalCount
    }
  }
`;

export const getShipstationShipmentsGql = gql`
  query getShipstationShipments(
    $limit: Int
    $offset: Int
    $filter: ShipstationShipmentFilter
    $sortBy: ShipstationShipmentSortBy
  ) {
    getShipstationShipments(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result {
        groupKey
        isBatchShipment
        orderId
        batchShipmentId
        trackingNumber
        recipientName
        shipmentStatus
        fulfillingPharmacyId
        partner {
          partnerUUID
          partnerName
        }
        practice {
          practiceId
          practiceName
        }
        createdAt
        itemCount
        lineItems {
          orderShipmentId
          orderId
          lineItemId
          trackingNumber
          shipmentStatus
          createdAt
          drugName
          sku
          quantity
        }
      }
    }
  }
`;

export const getShipstationShipmentCountsBatchGql = gql`
  query GetShipstationShipmentCountsBatch($queries: [ShipstationShipmentCountQuery!]!) {
    getShipstationShipmentCountsBatch(queries: $queries) {
      key
      total
      byPharmacy {
        fulfillingPharmacyId
        count
      }
    }
  }
`;

export const getShipmentCountsBatchGql = gql`
  query CountShipmentsBatch($queries: [ShipmentCountQuery!]!) {
    countShipmentsBatch(queries: $queries) {
      key
      buckets {
        fulfillingPharmacyId
        status
        count
      }
      totalCount
    }
  }
`;

export const listShipmentsFromPrxGql = gql`
  query ListShipments($limit: Int, $offset: Int, $filter: ShipmentFilter, $sortBy: ShipmentSort) {
    listShipments(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      shipments {
        shipmentId
        referenceId
        isManualShipment
        carrier
        carrierServiceCode
        carrierPackageCode
        cost
        status
        shippedTimestamp
        voidedTimestamp
        pickedUpTimestamp
        outForDeliveryTimestamp
        deliveredTimestamp
        trackingNumber
        createdTimestamp
        isBatchedExternal
        addressType
        isManualShipment
        clientMetadata
        recipientName
        fulfillingPharmacyId
        partnerName
        practiceName
        addressValidity
        addressDeficiencies
        originalCarrierServiceCode
        deliveryInstructions
        saturdayShipping
        signatureRequired
        shipDate
        shippingAddress {
          nameOnAddress
          phoneNumber
          addressDirectoryId
          address {
            line1
            line2
            city
            state
            zip
          }
        }
        orderWeight {
          value
          units
        }
        packageDimensions {
          length
          width
          height
          units
        }
        originalShippingAddress {
          addressDirectoryId
          address {
            line1
            line2
            city
            state
            zip
          }
        }
        validatedShippingAddress {
          nameOnAddress
          phoneNumber
          addressDirectoryId
          address {
            line1
            line2
            city
            state
            zip
          }
        }
        items {
          referenceId
          externalOriginReferenceId
          name
          sku
          quantity
          weight {
            value
            units
          }
        }
        tags {
          ruleId
          label
          color
          icon
          tooltip
        }
        notes {
          id
          content
          authorName
          authorId
          state
          createdAt
          contextType
          contextId
        }
      }
      totalCount
    }
  }
`;

export const getShipmentEventHistoryGql = gql`
  query getShipmentEventHistory($orderId: String!) {
    getShipmentEventHistory(orderId: $orderId) {
      count
      shipmentRequestId
      referenceId
      events {
        auditEventId
        auditEventType
        auditEventCode
        auditEventData
        shipmentRequestId
        referenceId
        createdTimestamp
        performedBy
        status
        shipmentSource
      }
      errors {
        auditEventId
        auditEventType
        auditEventCode
        auditEventData
        shipmentRequestId
        referenceId
        createdTimestamp
        performedBy
        status
        shipmentSource
      }
    }
  }
`;

export const setLineItemShipByDateGql = gql`
  mutation setLineItemShipByDate($input: SetLineItemShipByDateInput!) {
    setLineItemShipByDate(input: $input) {
      requestLineItemId
      shipByDate
      tags {
        tagName
        isSystemTag
      }
    }
  }
`;

export const clearShipByDateOverrideGql = gql`
  mutation clearShipByDateOverride($lineItemId: String!) {
    clearShipByDateOverride(lineItemId: $lineItemId) {
      requestLineItemId
      shipByDate
      tags {
        tagName
        isSystemTag
      }
    }
  }
`;

export const applyUrgentTagGql = gql`
  mutation applyUrgentTag($input: ApplyUrgentTagInput!) {
    applyUrgentTag(input: $input) {
      requestLineItemId
      shipByDate
      tags {
        tagName
        isSystemTag
      }
    }
  }
`;

export const removeUrgentTagGql = gql`
  mutation removeUrgentTag($input: RemoveUrgentTagInput!) {
    removeUrgentTag(input: $input) {
      requestLineItemId
      shipByDate
      tags {
        tagName
        isSystemTag
      }
    }
  }
`;

export const sendOrderBackToQueueGql = gql`
  mutation sendOrderBackToQueue($input: SendOrderBackToQueueInput!) {
    sendOrderBackToQueue(input: $input) {
      success
      skippedOrders {
        orderId
        reason
      }
    }
  }
`;

export const shipOrderLaterGql = gql`
  mutation shipOrderLater($input: ShipOrderLaterInput!) {
    shipOrderLater(input: $input) {
      orderId
    }
  }
`;
